import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '..';
import type { HubspotConfigurationItem } from '@server/hubspot-documentation';
import { HubspotParentType } from '@server/hubspot-documentation';
import {
  HUBSPOT_TABS_STORAGE_KEY,
  loadTabsForOrg,
  saveTabsForOrg,
} from '../../lib/documentationTabsLocalStorage';

type ElementsByType = Partial<Record<HubspotParentType, HubspotConfigurationItem[]>>;

export const HUBSPOT_DEFAULT_TAB = HubspotParentType.SCHEMA;

export const HUBSPOT_DEFAULT_REMOVABLE_TABS: HubspotParentType[] = [
  HubspotParentType.WORKFLOW,
  HubspotParentType.PIPELINE,
  HubspotParentType.LIST,
  HubspotParentType.FORM,
  HubspotParentType.MARKETING_EMAIL,
];

export const HUBSPOT_INITIAL_TABS: HubspotParentType[] = [
  HUBSPOT_DEFAULT_TAB,
  ...HUBSPOT_DEFAULT_REMOVABLE_TABS,
];

export interface HubspotDocumentationState {
  elementsByOrg: {
    [crmOrgId: string]: ElementsByType;
  };
  loadingByOrg: {
    [crmOrgId: string]: Partial<Record<HubspotParentType, boolean>>;
  };
  activeParentType: HubspotParentType;
  openTabs: HubspotParentType[];
}

const initialState: HubspotDocumentationState = {
  elementsByOrg: {},
  loadingByOrg: {},
  activeParentType: HUBSPOT_DEFAULT_TAB,
  openTabs: [...HUBSPOT_INITIAL_TABS],
};

export const hubspotDocumentationSlice = createSlice({
  name: 'hubspotDocumentation',
  initialState,
  reducers: {
    setHubspotElements: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        parentType: HubspotParentType;
        elements: HubspotConfigurationItem[];
      }>,
    ) => {
      const { crmOrgId, parentType, elements } = action.payload;
      if (!state.elementsByOrg[crmOrgId]) {
        state.elementsByOrg[crmOrgId] = {};
      }
      state.elementsByOrg[crmOrgId][parentType] = elements;
    },

    setHubspotLoading: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        parentType: HubspotParentType;
        isLoading: boolean;
      }>,
    ) => {
      const { crmOrgId, parentType, isLoading } = action.payload;
      if (!state.loadingByOrg[crmOrgId]) {
        state.loadingByOrg[crmOrgId] = {};
      }
      state.loadingByOrg[crmOrgId][parentType] = isLoading;
    },

    clearHubspotElements: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      delete state.elementsByOrg[action.payload.crmOrgId];
      delete state.loadingByOrg[action.payload.crmOrgId];
    },

    setHubspotActiveParentType: (state, action: PayloadAction<HubspotParentType>) => {
      state.activeParentType = action.payload;
    },

    loadHubspotTabsForOrg: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const stored = loadTabsForOrg<HubspotParentType[]>(HUBSPOT_TABS_STORAGE_KEY, action.payload.crmOrgId);
      if (stored?.length) {
        const withDefaults = [
          HUBSPOT_DEFAULT_TAB,
          ...stored.filter((t) => t !== HUBSPOT_DEFAULT_TAB),
        ];
        state.openTabs = withDefaults;
        state.activeParentType = withDefaults[0];
      }
    },

    addHubspotTab: (
      state,
      action: PayloadAction<{ parentType: HubspotParentType; crmOrgId: string }>,
    ) => {
      const { parentType, crmOrgId } = action.payload;
      if (!state.openTabs.includes(parentType)) {
        state.openTabs.push(parentType);
      }
      state.activeParentType = parentType;
      const userTabs = state.openTabs.filter((t) => t !== HUBSPOT_DEFAULT_TAB);
      saveTabsForOrg(HUBSPOT_TABS_STORAGE_KEY, crmOrgId, userTabs);
    },

    removeHubspotTab: (
      state,
      action: PayloadAction<{ parentType: HubspotParentType; crmOrgId: string }>,
    ) => {
      const { parentType, crmOrgId } = action.payload;
      if (parentType === HUBSPOT_DEFAULT_TAB) return;
      state.openTabs = state.openTabs.filter((t) => t !== parentType);
      if (state.activeParentType === parentType) {
        state.activeParentType = state.openTabs[state.openTabs.length - 1] ?? HUBSPOT_DEFAULT_TAB;
      }
      const userTabs = state.openTabs.filter((t) => t !== HUBSPOT_DEFAULT_TAB);
      saveTabsForOrg(HUBSPOT_TABS_STORAGE_KEY, crmOrgId, userTabs);
    },
  },
});

export const {
  setHubspotElements,
  setHubspotLoading,
  clearHubspotElements,
  setHubspotActiveParentType,
  addHubspotTab,
  removeHubspotTab,
  loadHubspotTabsForOrg,
} = hubspotDocumentationSlice.actions;

export const selectHubspotElements =
  (crmOrgId: string, parentType: HubspotParentType) => (state: RootState) =>
    state[hubspotDocumentationSlice.name]?.elementsByOrg[crmOrgId]?.[parentType];

export const selectHubspotLoading =
  (crmOrgId: string, parentType: HubspotParentType) => (state: RootState) =>
    state[hubspotDocumentationSlice.name]?.loadingByOrg[crmOrgId]?.[parentType] ?? false;

export const selectHubspotActiveParentType = (state: RootState) =>
  state[hubspotDocumentationSlice.name]?.activeParentType ?? HUBSPOT_DEFAULT_TAB;

export const selectHubspotOpenTabs = (state: RootState) =>
  state[hubspotDocumentationSlice.name]?.openTabs ?? [...HUBSPOT_INITIAL_TABS];

export default hubspotDocumentationSlice.reducer;
